<template>
    <div v-if="book">
        <v-row>
            <v-col cols="10">
                <v-card class="h-100">
                    <v-row>
                        <v-col cols="3">
                            <RouterLink :to="`/teacher/book/${book.bookId}/view`">
                                <v-img cover :src="getImageUrl(book.coverUrl, book)"></v-img>
                            </RouterLink>
                        </v-col>
                        <v-col class="d-flex flex-column">
                            <v-row class="flex-grow-0">
                                <v-col>
                                    <h2
                                        :class="{ r2l: book.properties.pageProgressionDirection == 'rtl' }"
                                        class="my-0 HeinemannSpecial-Roman"
                                    >
                                        {{ book.title }}
                                    </h2>
                                    <div class="d-flex justify-start">
                                        <template v-if="book.readingLevel">
                                            <img
                                                height="18"
                                                :src="`/common/img/level-${book.readingLevel.name.replaceAll(' ', '-').toLowerCase()}.png`"
                                                :alt="book.readingLevel.name"
                                            />
                                        </template>
                                        <div class="ml-1 text-grey">{{ bookDetailsSummaryText }}</div>
                                    </div>
                                    <div class="d-flex gc-2">
                                        <v-icon
                                            v-for="f in book.features"
                                            :key="`feature-${f.id}`"
                                            color="grey"
                                            size="18"
                                            :icon="featureIcons[f.id].icon"
                                            class="pt-1"
                                        />
                                        <v-icon color="grey" size="18" icon="mdi-circle-medium" class="pt-1" />
                                        <span class="text-grey">{{ purposeAndGenre }}</span>
                                    </div>
                                </v-col>
                                <v-col cols="2">
                                    <IconButton
                                        icon="mdi-heart"
                                        :active="isFavourite"
                                        class="pull-right"
                                        @click="favouritesStore.toggle(parseInt(book.bookId))"
                                    />
                                </v-col>
                            </v-row>

                            <div v-if="book.overview" class="mt-5 text-grey">
                                <p ref="bookOverviewRef">{{ book.overview }}</p>
                                <div
                                    v-if="showShowMoreBtn"
                                    class="show-more HeinemannSpecial-Bold mt-1"
                                    @click="toggleExpanded"
                                >
                                    Show {{ isOverviewExpanded ? 'less' : 'more' }}
                                </div>
                            </div>
                            <div class="d-flex gc-3 mt-auto">
                                <v-btn
                                    prepend-icon="mdi-account"
                                    color="brand"
                                    @click="showManageBookAccessDialog = true"
                                    >Manage access</v-btn
                                >
                                <RouterLink v-if="book.showAsEpub" :to="`/teacher/book/${book.bookId}/view-epub`">
                                    <v-btn variant="outlined" color="brand">Preview</v-btn>
                                </RouterLink>
                                <v-btn v-else variant="outlined" color="brand" :to="`/teacher/book/${book.bookId}/view`"
                                    >Preview</v-btn
                                >
                            </div>
                        </v-col>
                    </v-row>
                    <v-divider class="my-4"></v-divider>
                    <v-row>
                        <v-col>
                            <div v-if="allLanguages.length > 0" class="text-grey">
                                Language{{ allLanguages.length > 0 ? 's' : '' }}:
                                <template v-for="(l, idx) in allLanguages" :key="`lang-${idx}`">
                                    <template v-if="idx === 0">
                                        {{ languages[l.language_code] }}
                                    </template>
                                    <template v-else>
                                        <RouterLink :to="`/teacher/book/${l.book_id}/about`">{{
                                            languages[l.language_code]
                                        }}</RouterLink> </template
                                    >{{
                                        idx === allLanguages.length - 2
                                            ? ' and '
                                            : idx < allLanguages.length - 1
                                              ? ', '
                                              : ''
                                    }}
                                </template>
                            </div>
                            <template v-if="book.themes?.length">
                                <span class="text-grey">
                                    Themes: {{ book.themes.map((theme) => theme.name).join(', ') }}
                                </span></template
                            >
                            <template v-if="book.notes">{{ book.notes }}</template>
                        </v-col>
                    </v-row>
                </v-card>
            </v-col>
            <BookStats :book-assignments="bookAssignments" :book="book" />
        </v-row>

        <v-row>
            <v-col cols="10">
                <v-card>
                    <v-sheet height="60" class="d-flex justify-space-between align-center">
                        <v-sheet v-if="selectedTasks.length" class="py-2 px-6 rounded-lg" color="surface-container">
                            <v-icon class="mr-1" size="18">
                                <i style="vertical-align: middle" class="fa fa-fw fa-list-check mr-2"></i>
                            </v-icon>
                            {{ selectedTasks.length }} task{{ selectedTasks.length > 1 ? 's' : '' }} selected
                            <v-icon class="ml-2" size="18" icon="mdi-close" @click="selectedTasks = []"></v-icon
                        ></v-sheet>
                        <h2 v-else class="HeinemannSpecial-Roman my-0">Tasks</h2>
                        <v-btn
                            v-if="selectedTasks.length"
                            prepend-icon="mdi-account"
                            class="text-none"
                            color="purple"
                            @click="showManageTasksDialog = true"
                            >Assign to</v-btn
                        >
                        <v-btn
                            v-else
                            :to="`/teacher/create-task/${bookId}`"
                            prepend-icon="mdi-plus"
                            color="tertiary-container"
                            >Create new task</v-btn
                        >
                    </v-sheet>
                    <v-chip-group
                        v-if="sortedTasks.length"
                        v-model="selectedTaskTypes"
                        selected-class="bg-brand"
                        column
                        multiple
                    >
                        <v-chip
                            v-for="taskGroup in [{ name: 'Show all', tasks: [] as TaskOverview[] }].concat(sortedTasks)"
                            :key="taskGroup.name"
                            :value="taskGroup.name"
                        >
                            <template v-if="taskGroup.tasks.length" #prepend>
                                <i
                                    v-if="taskGroup.tasks[0].owner && taskGroup.tasks[0].owner.id !== 0"
                                    class="fa fa-user mr-1"
                                ></i>
                                <TaskIcon v-else class="mr-1" :task="taskGroup.tasks[0]"></TaskIcon>
                            </template>
                            {{ taskGroup.name }}</v-chip
                        >
                    </v-chip-group>
                    <v-data-table
                        v-if="tasks.length"
                        v-model="selectedTasks"
                        return-object
                        hover
                        :items-per-page="10"
                        :items-per-page-options="[10]"
                        show-select
                        :items="filteredTasks"
                        :headers="[
                            { title: 'Title', key: 'title' },
                            { key: 'reading_test', align: 'end', cellProps: { class: 'px-0' } },
                            { key: 'id', width: 130, cellProps: { class: 'px-0' } },
                        ]"
                    >
                        <template #headers> </template>
                        <!-- eslint-disable-next-line -->
                        <template #item.data-table-select="{ toggleSelect, internalItem, isSelected }">
                            <div v-if="internalItem.raw.types.some((t: TaskType) => t.code === 'teacher')">
                                <v-tooltip
                                    location="top"
                                    :open-delay="200"
                                    text="This is a teacher-led task"
                                    activator="parent"
                                />
                                <v-icon end icon="mdi-school"> </v-icon>
                            </div>
                            <v-checkbox-btn
                                v-else
                                :model-value="isSelected(internalItem)"
                                @update:model-value="toggleSelect(internalItem)"
                            />
                        </template>
                        <template #[`item.title`]="{ internalItem: { value: task } }">
                            <div
                                :class="[taskCanBePreviewed(task) ? 'cursor-pointer' : 'cursor-default']"
                                @click="showPreview(task)"
                            >
                                <v-tooltip
                                    v-if="taskCanBePreviewed(task)"
                                    open-delay="200"
                                    location="top left"
                                    activator="parent"
                                    >Preview</v-tooltip
                                >
                                {{ truncateTitle(task.title) }}
                                <div v-if="task.owner && task.owner.id !== 0" class="d-inline-flex ml-1 gc-1">
                                    <i class="fa fa-user"></i><span>({{ taskTypeName(task) }})</span>
                                </div>
                            </div>
                        </template>
                        <template #[`item.reading_test`]="item">
                            <RouterLink
                                v-if="item.value && taskHasResults(item.item)"
                                class="text-decoration-none text-black"
                                :to="`/teacher/reporting-question-set?taskId=${item.item.id}`"
                            >
                                <v-btn append-icon="mdi-chart-line" variant="text">Results</v-btn>
                            </RouterLink>
                        </template>
                        <template #[`item.id`]="{ value: taskId }">
                            <RouterLink
                                class="text-decoration-none text-black"
                                :to="`/teacher/task/${taskId}/view-task`"
                            >
                                <v-btn append-icon="mdi-chevron-right" variant="text">View task</v-btn>
                            </RouterLink>
                        </template>
                        <template v-if="tasks.length <= 10" #bottom></template>
                    </v-data-table>
                    <p v-else>There are no tasks for this book yet.</p>
                    <TeacherManageTasksDialog
                        v-model="showManageTasksDialog"
                        :book="book"
                        :preSelectedTasks="selectedTasks"
                    ></TeacherManageTasksDialog>
                </v-card>
            </v-col>
            <TaskStats
                heading="Tasks"
                :no-of-submitted="noOfSubmittedTasks"
                :no-of-assigned="noOfAssignedTasks"
                :book="book"
                :no-of-completed="noOfCompletedTasks"
            />

            <v-col v-if="resources.length > 0" cols="12">
                <v-card>
                    <h2 class="HeinemannSpecial-Roman">Additional Resources</h2>
                    <v-row>
                        <v-col v-for="(resource, idx) of resources" :key="`resource-${idx}`" cols="4">
                            <TeacherAdditionalResourcesCard :resource="resource" />
                        </v-col>
                    </v-row>
                </v-card>
            </v-col>
            <v-col cols="12">
                <v-card v-if="otherbooks.length > 0">
                    <h2 class="HeinemannSpecial-Roman">Related Books</h2>
                    <div class="grid no-gutters carousel">
                        <template v-for="(obook, i) of otherbooks" :key="`obook-${obook.bookId}`">
                            <div class="unit one-quarter book">
                                <RouterLink :to="`/teacher/book/${obook.bookId}/about`">
                                    <img class="cover" :src="getImageUrl(obook.coverUrl, obook)" alt="" />
                                </RouterLink>
                                <div class="book-title">
                                    <RouterLink :to="`/teacher/book/${obook.bookId}/about`">{{
                                        obook.title
                                    }}</RouterLink>
                                </div>
                                <TeacherBookDetails :book="obook" />
                            </div>
                            <br v-if="i > 0 && i % 4 == 3" style="clear: both" />
                        </template>
                    </div>
                </v-card>
            </v-col>
        </v-row>
    </div>
    <TeacherManageAccessDialog v-if="book" v-model="showManageBookAccessDialog" :books="[book]" />
    <TeacherTaskPreview v-if="showTaskPreview" v-model="showTaskPreviewDialog" :taskOverview="showTaskPreview" />
</template>
<script setup lang="ts">
import { storeToRefs } from 'pinia';
import IconButton from '~/components/IconButton.vue';
import BookStats from '~/components/teacher/sidepanel/BookStats.vue';
import TaskStats from '~/components/teacher/sidepanel/TaskStats.vue';
import { BookResource, TaskOverview, TaskType } from '@ilteducation/giglets-types';
import { GigletsBookV1 } from '@ilteducation/content-registry-types';

const numOfOverviewLinesToShow = ref(4);
const totalNumberOfLines = ref(0);

const route = useRoute('/teacher/book/[book_id]/about');
definePage({ meta: { area: 'view-book' } });
const languages = useLanguageNames();
const featureIcons = useFeatureIcons();
const { getImageUrl } = useImageCacheReset();
const showManageBookAccessDialog = ref(false);

const store = useTeacherStore();
store.fetchSchool();
store.fetchPupilTasks();
store.fetchPupils();
const { school, pupils, pupilTasks } = storeToRefs(store);
const assignedBooksStore = useAssignedBooks();
assignedBooksStore.fetchAssignedBooks('teacher', []);
const { assignedBooks } = storeToRefs(assignedBooksStore);

const bookAssignments = computed(() =>
    (assignedBooks.value.find((ab) => ab.book_id === bookId.value)?.assigned_to || []).map((a) => {
        const pupil = pupils.value.find((p) => p.id === a.id);
        return {
            ...a,
            name: pupil ? pupil.name : '',
        };
    }),
);
const pupilTasksForBook = computed(() => pupilTasks.value.filter((pt) => pt.task.book.id === bookId.value));
const noOfAssignedTasks = computed(() => pupilTasksForBook.value.filter((pt) => pt.status === 1).length);
const noOfSubmittedTasks = computed(() => pupilTasksForBook.value.filter((pt) => pt.status === 2).length);
const noOfCompletedTasks = computed(() => pupilTasksForBook.value.filter((pt) => pt.status >= 3).length);

const bookId = ref(parseInt(route.params.book_id + '') || 0);
const showTaskPreviewDialog = ref(false);
const showTaskPreview = ref<TaskOverview | null>(null);

const showPreview = (task: TaskOverview) => {
    showTaskPreview.value = task;
    showTaskPreviewDialog.value = true;
};

const taskCanBePreviewed = (task: TaskOverview) =>
    task.hots || task.reading_test || task.types.map((tt) => tt.code).includes('before');
const taskHasResults = (task: TaskOverview) => {
    return pupilTasksForBook.value.some((pt) => pt.task.id === task.id && pt.status >= 2);
};

const contentSearchStore = useContentSearchStore();
const book = await contentSearchStore.getBook(bookId.value + '');
const { changeBackground } = useBackground();
if (book?.backgroundUrl) {
    changeBackground(getImageUrl(book.backgroundUrl, book), 'revert-layer', false, true);
}
const bookDetailsSummaryText = computed(() => {
    if (!book) {
        return '';
    }
    const info = [];
    if (book.readingLevel) {
        info.push(book.readingLevel.name);
    }
    if (book.readingAge.min) {
        info.push(
            'reading age ' +
                (book.readingAge.max ? book.readingAge.min + '-' + book.readingAge.max : book.readingAge.min),
        );
    }
    if (book.interestAge.min) {
        info.push(
            'interest age ' +
                (book.interestAge.max ? book.interestAge.min + '-' + book.interestAge.max : book.interestAge.min),
        );
    }
    return info.join(', ');
});
const purposeAndGenre = computed(() => {
    if (!book) {
        return '';
    }
    const info = [];
    if (book.purposes?.[0]) {
        info.push(book.purposes[0].name);
    }
    if (book.genres?.[0]) {
        info.push(book.genres[0].name);
    }
    return info.join(', ');
});
fetchOtherBooks();

const favouritesStore = useFavouritesStore();
const { favourites } = storeToRefs(favouritesStore);
const isFavourite = computed(() => favourites.value.includes(bookId.value));

const allLanguages = computed(() => {
    if (!book) {
        return [];
    }
    const trans = book.languages?.translations || [];
    return [
        { book_id: book.id, language_code: book.languages.mainLanguage },
        ...trans.map((l) => ({ book_id: l.bookId, language_code: l.language })),
    ];
});

const previewItems = ref<any[]>([]);
$fetch('/api/book/' + bookId.value + '/preview').then((res) => (previewItems.value = res as any[]));

const tasks = ref<TaskOverview[]>([]);
$fetch<TaskOverview[]>('/api/book/' + bookId.value + '/tasks').then(
    (res) => (tasks.value = res.filter((t) => t.published === 1)),
);

const resources = ref<BookResource[]>([]);
$fetch<BookResource[]>('/api/book/' + bookId.value + '/resources').then((res) => (resources.value = res));

function groupAndSort(bag: TaskOverview[]) {
    const groupedTasks: { [id: string]: { name: string; tasks: TaskOverview[] } } = {};
    const addToGroup = function (group: string, task: TaskOverview) {
        if (!groupedTasks[group]) {
            groupedTasks[group] = { tasks: [], name: group };
        }
        groupedTasks[group].tasks.push(task);
    };
    for (const task of bag) {
        if (task.owner && task.owner.id !== 0) {
            addToGroup('Tasks created by you', task);
        } else {
            addToGroup(taskTypeName(task), task);
        }
    }
    const list = Object.values(groupedTasks);
    list.sort((a, b) => {
        if (a.name === 'Before Questions') {
            return -1;
        }
        if (b.name === 'Before Questions') {
            return 1;
        }
        if (a.name === 'HOTS Questions') {
            return -1;
        }
        if (b.name === 'HOTS Questions') {
            return 1;
        }
        if (a.name === 'ASPIRE Questions') {
            return -1;
        }
        if (b.name === 'ASPIRE Questions') {
            return 1;
        }
        if (a.name === 'Reading Test Questions') {
            return -1;
        }
        if (b.name === 'Reading Test Questions') {
            return 1;
        }
        if (a.name === 'Tasks created by you') {
            return -1;
        }
        if (b.name === 'Tasks created by you') {
            return 1;
        }
        return a.name.localeCompare(b.name);
    });
    return list;
}

const sortedTasks = computed(() => {
    return groupAndSort(tasks.value);
});

const otherbooks = ref<GigletsBookV1[]>([]);
function fetchOtherBooks() {
    if (!book || !book.otherBookIds) {
        return;
    }
    contentSearchStore.getBooks(book.otherBookIds).then((books) =>
        books.forEach((ob) => {
            if (!ob) {
                return;
            }
            if (ob.state !== 'published') {
                return;
            }
            if (
                school.value &&
                school.value.type.abbrv === 'WS' &&
                !ob.accessRules.subscriptionType.includes('world-stories')
            ) {
                return;
            }
            otherbooks.value.push(ob);
        }),
    );
}

const bookOverviewRef = ref<HTMLElement | null>(null);
const showShowMoreBtn = ref(false);
const isOverviewExpanded = ref(false);

const toggleExpanded = () => {
    const totalNumOfLines = Math.ceil(
        bookOverviewRef.value!.scrollHeight / parseInt(window.getComputedStyle(bookOverviewRef.value!).lineHeight),
    );
    if (totalNumOfLines > totalNumberOfLines.value) {
        totalNumberOfLines.value = totalNumOfLines;
    }
    isOverviewExpanded.value = !isOverviewExpanded.value;
    bookOverviewRef.value?.classList.toggle('clamped');
};

const unwatch = watch(
    () => bookOverviewRef.value,
    async (po) => {
        if (po) {
            unwatch();
            await nextTick();
            const numOfLines = Math.ceil(po.scrollHeight / parseInt(window.getComputedStyle(po).lineHeight));
            totalNumberOfLines.value = numOfLines;
            if (numOfLines > numOfOverviewLinesToShow.value) {
                po.classList.add('blurb');
                po.classList.add('clamped');
                showShowMoreBtn.value = true;
            }
        }
    },
);

const selectedTaskTypes = ref<string[]>(['Show all']);
watch(selectedTaskTypes, (newSelection, oldSelection) => {
    if (newSelection.includes('Show all')) {
        if (!oldSelection.includes('Show all')) {
            selectedTaskTypes.value = ['Show all'];
        } else if (newSelection.length > 1) {
            selectedTaskTypes.value = newSelection.filter((s) => s !== 'Show all');
        }
    } else if (newSelection.length === 0) {
        selectedTaskTypes.value = ['Show all'];
    }
});
const filteredTasks = computed(() => {
    if (selectedTaskTypes.value.length === 0 || selectedTaskTypes.value.includes('Show all')) {
        return sortedTasks.value.flatMap((task) => task.tasks);
    }
    return sortedTasks.value
        .filter((task) => selectedTaskTypes.value.includes(task.name))
        .flatMap((task) => task.tasks);
});
const showManageTasksDialog = ref(false);
const selectedTasks = ref<TaskOverview[]>([]);

const taskTypeName = (task: TaskOverview) => {
    if (task.types.map((tt) => tt.code).includes('before')) {
        return 'Before Questions';
    }
    if (task.hots) {
        return 'HOTS Questions';
    }
    if (task.aspire) {
        return 'ASPIRE Questions';
    }
    if (task.reading_test) {
        return 'Reading Test Questions';
    }
    if (school.value?.curriculum.id === 1 && task.level) {
        return 'Cross-Curricular Tasks - ' + task.level.title;
    }
    if (school.value?.curriculum.id === 2 && task.year_group) {
        return 'Cross-Curricular Tasks - ' + task.year_group.name;
    }
    if (school.value?.curriculum.id === 3 && task.year) {
        return 'Cross-Curricular Tasks - ' + task.year.name;
    }
    if (school.value?.curriculum.id === 4 && task.irish_stage) {
        return 'Cross-Curricular Tasks - ' + task.irish_stage.name_en;
    }
    return 'Cross-Curricular Tasks';
};

const truncateTitle = (title = '', maxLength = 40) => {
    return title.length > maxLength ? title.substring(0, maxLength) + '...' : title;
};

watch(showManageTasksDialog, (show) => {
    if (!show) {
        selectedTasks.value = [];
    }
});
</script>
<style scoped lang="scss">
button {
    font-size: 14px;
    letter-spacing: normal;
}

p.blurb {
    transition: height 0.4s ease-in-out;
    overflow: hidden;

    &:not(.clamped) {
        height: calc(1.5em * v-bind('totalNumberOfLines'));
    }

    &.clamped {
        height: calc(1.5em * v-bind('numOfOverviewLinesToShow'));
        display: -webkit-box;
        -webkit-line-clamp: v-bind('numOfOverviewLinesToShow');
        -webkit-box-orient: vertical;
        text-overflow: ellipsis;
    }
}

.show-more {
    display: inline-flex;
    float: right;
    cursor: pointer;
    font-size: 0.9em;
}

:deep(table) {
    color: unset;
}
</style>
